import type {
  EmailSignatureV2,
  TeamEmailSignatureBulkCreateRequest,
} from '@components/EmailSignaturev2/types';
import { SendEmailSignatureBulkCreateCsvDocument } from '@graphql/mutations/email-signature/send-email-signature-bulk-create-csv.graphql';
import { collections, functions } from '@modules/firebase';
import { logger } from '@modules/logger';
import {
  sanitizeForDb,
  toDBEmailSignatureV2,
  toDBTeamEmailSignatureBulkCreateRequest,
  toEmailSignatureV2,
  toTeamEmailSignatureBulkCreateRequest,
} from '@modules/transformers';
import { assertIsType, assertIsTypedArray } from '@modules/types';
import { apolloClient } from '@services/apollo';
import {
  and,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  or,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import {
  isDBEmailSignatureV2,
  isDBTeamEmailSignatureBulkCreateRequest,
} from './predicates/email-signature-predicates';

export async function saveEmailSignature(emailSignature: EmailSignatureV2): Promise<void> {
  const docRef = doc(collections.emailSignatures(), emailSignature.id);

  const dbEmailSignature = toDBEmailSignatureV2(emailSignature);
  const sanitisedDbEmailSignature = sanitizeForDb(dbEmailSignature);
  return setDoc(docRef, sanitisedDbEmailSignature, { merge: false });
}

export function subscribeToEmailSignature(
  emailSignatureId: string,
  onUpdate: (emailSignature: EmailSignatureV2) => void,
): () => void {
  const docRef = doc(collections.emailSignatures(), emailSignatureId);

  const subscribe = onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) return;

    const emailSignature = snapshot.data();
    assertIsType(emailSignature, isDBEmailSignatureV2);
    onUpdate(toEmailSignatureV2(emailSignature));
  });

  return subscribe;
}

export async function createTeamEmailSignatureBulkCreateRequest(
  emailSignatureBulkCreateRequest: TeamEmailSignatureBulkCreateRequest,
): Promise<void> {
  const docRef = doc(collections.teamEmailSignatureRequests(), emailSignatureBulkCreateRequest.id);

  const dbTeamEmailSignatureBulkCreateRequest = toDBTeamEmailSignatureBulkCreateRequest(
    emailSignatureBulkCreateRequest,
  );

  return setDoc(docRef, dbTeamEmailSignatureBulkCreateRequest, { merge: false });
}

export function subscribeToTeamEmailSignatureBulkCreateRequest(
  requestId: string,
  onUpdate: (emailSignatureBulkCreateRequest: TeamEmailSignatureBulkCreateRequest) => void,
): () => void {
  const docRef = doc(collections.teamEmailSignatureRequests(), requestId);
  const subscribe = onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) return;

    const emailSignatureBulkCreateRequest = snapshot.data();
    assertIsType(emailSignatureBulkCreateRequest, isDBTeamEmailSignatureBulkCreateRequest);
    onUpdate(toTeamEmailSignatureBulkCreateRequest(emailSignatureBulkCreateRequest));
  });

  return subscribe;
}

export function subscribeToUserEmailSignatures(
  userId: string,
  onUpdate: (emailSignatures: Array<EmailSignatureV2>) => void,
  orgId?: string,
  recipientEmail?: string,
): () => void {
  const userWhere = where('user_id', '==', userId);
  const orgWhere = where('org_id', '==', orgId);
  const recipientEmailWhere = where('recipient_email', '==', recipientEmail);

  const docsQuery =
    recipientEmail && orgId
      ? query(collections.emailSignatures(), or(userWhere, and(orgWhere, recipientEmailWhere)))
      : query(collections.emailSignatures(), or(userWhere));

  const unsubscribe = onSnapshot(docsQuery, (snapshot) => {
    const emailSignatures = snapshot.docs.map((doc) => doc.data());
    assertIsTypedArray(emailSignatures, isDBEmailSignatureV2);

    onUpdate(emailSignatures.map((emailSignature) => toEmailSignatureV2(emailSignature)));
  });

  return unsubscribe;
}

export function subscribeToAllBusinessEmailSignatures(
  orgId: string,
  onUpdate: (emailSignatures: Array<EmailSignatureV2>) => void,
): () => void {
  const docsQuery = query(collections.emailSignatures(), where('org_id', '==', orgId));

  const unsubscribe = onSnapshot(docsQuery, (snapshot) => {
    const emailSignatures = snapshot.docs.map((doc) => doc.data());
    assertIsTypedArray(emailSignatures, isDBEmailSignatureV2);

    onUpdate(emailSignatures.map((emailSignature) => toEmailSignatureV2(emailSignature)));
  });

  return unsubscribe;
}

export async function sendEmailSignatureBulkCreateCsv(
  emailSignature: EmailSignatureV2,
  requestId: string,
  orgId: string,
  recipientEmail: string,
): Promise<void> {
  logger.info('Sending email signature bulk create csv', { org_id: orgId, request_id: requestId });

  await apolloClient.mutate({
    mutation: SendEmailSignatureBulkCreateCsvDocument,
    variables: {
      input: {
        orgId,
        requestId,
        recipientEmail,
        emailSignature: JSON.stringify(toDBEmailSignatureV2(emailSignature)),
      },
    },
  });
}

export async function deleteEmailSignature(id: string): Promise<void> {
  const docRef = doc(collections.emailSignatures(), id);

  return deleteDoc(docRef);
}

/**
 * Send the signature to the user, this function is currently used for sending testing signature and sending signature for iphone setup
 * @param emailSignatureId
 * @param testEmailSignature If the test signature object is provided, the cloud function will generate the image and store it in temp folder before sending the email
 */
export async function sendEmailSignatureToUser({
  emailSignatureId,
  testEmailSignature, // if testEmailSignature is provided the function will generate the image for the signature and then send the email
}: {
  emailSignatureId?: string;
  testEmailSignature?: EmailSignatureV2; // if testEmailSignature is provided the function will generate the image for the signature and then send the email
}): Promise<void> {
  logger.info('Calling firebase function', { function: 'sendEmailSignatureHtml' });
  const sendEmailSignatureHtml = httpsCallable(functions, 'sendEmailSignatureHtml');

  await sendEmailSignatureHtml({
    email_signature_id: emailSignatureId,
    test_email_signature: testEmailSignature ? toDBEmailSignatureV2(testEmailSignature) : undefined,
  });

  return;
}

/**
 * Get email signatures by user's ID
 * @param id ID of the user
 */
export async function getEmailSignaturesByUserId(userId: string): Promise<EmailSignatureV2[]> {
  const docsQuery = query(collections.emailSignatures(), where('user_id', '==', userId));

  const snapshot = await getDocs(docsQuery);
  const dbEmailSignatures = snapshot.docs.map((doc) => doc.data());
  assertIsTypedArray(dbEmailSignatures, isDBEmailSignatureV2);

  return dbEmailSignatures.map((dbEmailSignature) => toEmailSignatureV2(dbEmailSignature));
}

/**
 * Gets the count of all email signatures for an org. This includes personal email signatures which
 * are part of the org, i.e. has the org_id and a the user_id field.
 */
export async function getEmailSignatureCount(orgId: string): Promise<number> {
  const docsQuery = query(collections.emailSignatures(), where('org_id', '==', orgId));

  const snapshot = await getDocs(docsQuery);
  return snapshot.size;
}
